import React, { useState } from 'react';
import {
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  Brain,
  Palette,
  Heart,
  Users,
  Footprints,
  Moon,
  Sofa,
  Monitor,
  Smile,
  Meh,
  Frown,
  SmilePlus,
  Annoyed
} from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import MoodModal from './MoodModal';
import StateModal from './StateModal';

/**
 * Check if state is stale (needs fresh check-in)
 * Stale = 6+ hours old OR from a different day
 */
export function isStateStale(timestamp) {
  if (!timestamp) return true;
  
  const stateTime = new Date(timestamp);
  const now = new Date();
  
  // Different day = stale
  if (stateTime.toDateString() !== now.toDateString()) {
    return true;
  }
  
  // 6+ hours old = stale
  const hoursSince = (now - stateTime) / (1000 * 60 * 60);
  if (hoursSince >= 6) {
    return true;
  }
  
  return false;
}

// Mood icons
const MOOD_ICONS = [
  { level: 1, icon: Frown },
  { level: 2, icon: Annoyed },
  { level: 3, icon: Meh },
  { level: 4, icon: Smile },
  { level: 5, icon: SmilePlus }
];

// Energy options
const ENERGY_OPTIONS = [
  { value: 'Low', icon: BatteryLow, color: ABLE_COLORS.energyLevel.low },
  { value: 'Med', icon: BatteryMedium, color: ABLE_COLORS.energyLevel.med },
  { value: 'High', icon: BatteryFull, color: ABLE_COLORS.energyLevel.high }
];

// Focus options
const FOCUS_OPTIONS = [
  { value: 'Creative', label: 'Creative', icon: Palette, color: ABLE_COLORS.state.creative },
  { value: 'Thinking', label: 'Thinking', icon: Brain, color: ABLE_COLORS.state.mental },
  { value: 'Wellbeing', label: 'Wellbeing', icon: Heart, color: ABLE_COLORS.state.emotional },
  { value: 'Social', label: 'Social', icon: Users, color: ABLE_COLORS.state.social },
  { value: 'Physical', label: 'Physical', icon: Footprints, color: ABLE_COLORS.state.physical },
  { value: 'Rest', label: 'Rest', icon: Moon, color: ABLE_COLORS.state.rest }
];

// Space options
const SPACE_OPTIONS = [
  { value: 'Couch', label: 'Couch', icon: Sofa, color: ABLE_COLORS.location.couch },
  { value: 'Desk', label: 'Desk', icon: Monitor, color: ABLE_COLORS.location.desk },
  { value: 'Moving', label: 'Moving', icon: Footprints, color: ABLE_COLORS.location.moving }
];

/**
 * Single state icon button
 */
function StateIcon({ icon: Icon, color, isSet, isStale, showPulse, onClick, title }) {
  // If stale, show muted even if value exists
  const displayColor = (isSet && !isStale) ? color : ABLE_COLORS.textMuted;
  const showIndicator = showPulse && isStale;
  
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        position: 'relative',
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: (isSet && !isStale) ? `${color}15` : 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s ease',
      }}
    >
      <Icon size={18} color={displayColor} strokeWidth={2} />
      
      {/* Pulse indicator for stale state */}
      {showIndicator && (
        <span
          style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: ABLE_COLORS.accent,
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
      )}
    </button>
  );
}

/**
 * CompactStateBar - Horizontal row of 4 state icons for header
 * Shows muted icons when state is stale (needs fresh check-in)
 */
export default function CompactStateBar({ currentState, onStateChange, onToast }) {
  const [activeModal, setActiveModal] = useState(null);
  
  const isStale = isStateStale(currentState?.timestamp);
  
  // Get mood display
  const getMoodDisplay = () => {
    const mood = currentState?.mood;
    if (!mood) {
      // Backwards compatibility
      if (currentState?.emotion) {
        const emotionMap = { 'Rough': 1, 'Meh': 2, 'Okay': 3, 'Good': 4, 'Great': 5 };
        const level = emotionMap[currentState.emotion] || 3;
        const moodIcon = MOOD_ICONS.find(m => m.level === level)?.icon || Meh;
        return { icon: moodIcon, color: ABLE_COLORS.mood[level], isSet: true };
      }
      return { icon: Smile, color: '#9CA3AF', isSet: false };
    }
    
    const moodIcon = MOOD_ICONS.find(m => m.level === mood.level)?.icon || Meh;
    const color = ABLE_COLORS.mood[mood.level] || ABLE_COLORS.mood[3];
    return { icon: moodIcon, color, isSet: true };
  };

  // Get energy display
  const getEnergyDisplay = () => {
    const energy = currentState?.energy;
    if (!energy) {
      return { icon: BatteryMedium, color: '#9CA3AF', isSet: false };
    }
    const opt = ENERGY_OPTIONS.find(o => o.value === energy);
    return { 
      icon: opt?.icon || BatteryMedium, 
      color: opt?.color || '#9CA3AF',
      isSet: true 
    };
  };

  // Get focus display
  const getFocusDisplay = () => {
    let intents = [];
    if (Array.isArray(currentState?.intents) && currentState.intents.length > 0) {
      intents = currentState.intents;
    } else if (currentState?.intent) {
      intents = [currentState.intent];
    } else if (currentState?.focus) {
      intents = [currentState.focus];
    }
    intents = intents.filter(i => i && typeof i === 'string' && i.trim() !== '');

    if (intents.length === 0) {
      return { icon: Brain, color: '#9CA3AF', isSet: false };
    }

    const first = FOCUS_OPTIONS.find(o => o.value === intents[0]);
    return {
      icon: first?.icon || Brain,
      color: first?.color || '#9CA3AF',
      isSet: true
    };
  };

  // Get space display
  const getSpaceDisplay = () => {
    const location = currentState?.location;
    if (!location || location.startsWith('Other')) {
      return { icon: Sofa, color: '#9CA3AF', isSet: false };
    }
    const opt = SPACE_OPTIONS.find(o => o.value === location);
    return { 
      icon: opt?.icon || Sofa, 
      color: opt?.color || '#9CA3AF',
      isSet: true 
    };
  };

  const moodDisplay = getMoodDisplay();
  const energyDisplay = getEnergyDisplay();
  const focusDisplay = getFocusDisplay();
  const spaceDisplay = getSpaceDisplay();

  // Any value missing = show pulse on first icon
  const anyMissing = !moodDisplay.isSet || !energyDisplay.isSet || !focusDisplay.isSet || !spaceDisplay.isSet;

  // Handle state updates
  const handleMoodChange = (mood) => {
    const newState = {
      ...currentState,
      mood: mood || undefined,
      emotion: undefined,
      feeling: undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(mood ? 'Got it ✓' : 'Cleared ✓');
  };

  const handleEnergyChange = (energy) => {
    const newState = {
      ...currentState,
      energy: energy || undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(energy ? 'Got it ✓' : 'Cleared ✓');
  };

  const handleFocusChange = (intents) => {
    const hasIntents = Array.isArray(intents) && intents.length > 0;
    const newState = {
      ...currentState,
      intents: hasIntents ? intents : undefined,
      intent: undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(hasIntents ? 'Got it ✓' : 'Cleared ✓');
  };

  const handleSpaceChange = (location) => {
    const newState = {
      ...currentState,
      location: location || undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(location ? 'Got it ✓' : 'Cleared ✓');
  };

  return (
    <>
      <div 
        className="compact-state-bar"
        style={{
          display: 'flex',
          gap: '2px',
          background: ABLE_COLORS.bgSubtle,
          borderRadius: '10px',
          padding: '4px',
        }}
      >
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
          }
          @media (max-width: 360px) {
            .compact-state-bar {
              display: none !important;
            }
          }
        `}</style>
        
        <StateIcon
          icon={moodDisplay.icon}
          color={moodDisplay.color}
          isSet={moodDisplay.isSet}
          isStale={isStale}
          showPulse={isStale || anyMissing}
          onClick={() => setActiveModal('mood')}
          title="Mood"
        />
        <StateIcon
          icon={energyDisplay.icon}
          color={energyDisplay.color}
          isSet={energyDisplay.isSet}
          isStale={isStale}
          showPulse={false}
          onClick={() => setActiveModal('energy')}
          title="Energy"
        />
        <StateIcon
          icon={focusDisplay.icon}
          color={focusDisplay.color}
          isSet={focusDisplay.isSet}
          isStale={isStale}
          showPulse={false}
          onClick={() => setActiveModal('focus')}
          title="Focus"
        />
        <StateIcon
          icon={spaceDisplay.icon}
          color={spaceDisplay.color}
          isSet={spaceDisplay.isSet}
          isStale={isStale}
          showPulse={false}
          onClick={() => setActiveModal('space')}
          title="Space"
        />
      </div>

      {/* Mood Modal */}
      {activeModal === 'mood' && (
        <MoodModal
          currentMood={currentState?.mood}
          onSave={handleMoodChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Energy Modal */}
      {activeModal === 'energy' && (
        <StateModal
          title="How's your energy?"
          options={ENERGY_OPTIONS}
          selected={currentState?.energy}
          multiSelect={false}
          onSave={handleEnergyChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Focus Modal */}
      {activeModal === 'focus' && (
        <StateModal
          title="What's your focus?"
          options={FOCUS_OPTIONS}
          selected={currentState?.intents || (currentState?.intent ? [currentState.intent] : [])}
          multiSelect={true}
          onSave={handleFocusChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Space Modal */}
      {activeModal === 'space' && (
        <StateModal
          title="Where are you?"
          options={SPACE_OPTIONS}
          selected={currentState?.location}
          multiSelect={false}
          onSave={handleSpaceChange}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}
